import { FC } from 'react';
import Page, { PageProps } from '../../../components/page';
import { Box, Stack } from '@mui/material';
import DataTable from '../../../components/data-table';
import PageHeader from '../../../components/page/header';
import Button from '../../../components/button';
import { DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import { getTelemetry } from '@/lib/services/telemetry.service';
import useFeature from '@/hooks/use-feature.hook';
import Tooltip from '@/components/tooltip';
import PageFeatureToast from '@/components/page-feature-toast';
import { useQuery } from 'react-query';
import { getSiemConnectors } from '@/lib/services/connector.service';
import useQueryHelper from '@/hooks/use-query-helper';
import NoneChip from '@/components/chip/none-chip';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import useDrawer from '@/hooks/use-drawer.hook';
import ConnectorLink from '@/components/connector-link';
import TelemetryIcon from '@/components/telemetry-icon';
import Text from '@/components/text';

const columns: DtColumn[] = [
  { name: 'displayName', label: 'Name', sortable: true, flex: 2 },
  { name: 'type', label: 'Type', sortable: false, flex: 1.8 },
  { name: 'userEvents', label: 'User Events', sortable: false },
  { name: 'auditEvents', label: 'Audit Events', sortable: false },
  { name: 'enabled', label: 'Activation', sortable: true },
  { name: 'status', label: 'Status', sortable: true },
  { name: 'action', label: '', sortable: false },
];

const TelemetryPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();

  const { canChangeTelemetry, getTooltip, loading: featureLoading } = useFeature();

  const connectorQuery = useQuery([QueryKey.ConnectorSiemsList], async () => getSiemConnectors());
  const { showLoader: connectorsLoading } = useQueryHelper(connectorQuery);
  const { data: connectors } = connectorQuery;

  const handleView = (id?: string) => {
    openDrawer('telemetry', { id });
  };

  const loadData = async (pageParams: DtPage, filter?: DtFilter) => {
    const { page, pageSize, sort } = pageParams;
    const dataset = await getTelemetry(page, pageSize, sort, filter);

    return {
      ...dataset,
      rows: dataset.rows.map((telemetry) => {
        const { id, displayName, connectorId, status, enabled, telemetryTypes, typeLabel } =
          telemetry;
        const connector = connectors?.find(({ id }) => id === connectorId);
        const hasConnector = !!connector;

        return {
          id,
          displayName: <Box maxWidth="100%">{displayName}</Box>,
          connector: hasConnector ? (
            <ConnectorLink connector={connector} action="none" />
          ) : (
            <NoneChip notAvailable />
          ),
          type: (
            <Stack direction="row" alignItems="center" gap={1}>
              <TelemetryIcon telemetry={telemetry} size="small" />
              <Text size="small" fixLineHeight>
                {typeLabel}
              </Text>
            </Stack>
          ),
          userEvents: <StatusChip value={telemetryTypes.userEvents.enabled} size="small" />,
          auditEvents: <StatusChip value={telemetryTypes.auditEvents.enabled} size="x-small" />,
          status: <StatusChip value={status as ChipStatus} size="x-small" />,
          enabled: <StatusChip value={enabled} size="x-small" />,
          action: <OpenDrawerButton onClick={() => handleView(id)} />,
        };
      }),
    };
  };

  if (featureLoading || connectorsLoading) {
    return null;
  }

  return (
    <Page title="Telemetry Destination">
      <PageHeader>
        <PageBlurb>
          Create new telemetry destinations, and view and change settings for existing destinations.
        </PageBlurb>
        <PageTools>
          <Tooltip title={getTooltip('add-telemetry')} disabled={canChangeTelemetry}>
            <Button
              label="Add Destination"
              icon="plus"
              size="small"
              onClick={() => handleView()}
              disabled={!canChangeTelemetry}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="change-telemetry" can={canChangeTelemetry} />
      <DomSize>
        <DataTable
          queryKey={QueryKey.TelemetryDataset}
          columns={columns}
          onLoad={loadData}
          search
          searchPlaceholder="Search by name"
          sort={{ columnName: 'displayName', direction: 'asc' }}
        />
      </DomSize>
    </Page>
  );
};

export default TelemetryPage;
